<template>
  <div class="pp-feed pa-feed-guest">
    <div class="head">
      <div>
        <div class="eyebrow">{{ t('property.v2News.projectUpdatesEyebrow') }}</div>
        <h3>{{ t('property.access.projectNewsFeedTeaser.newsOn', { project: projectTitle }) }}</h3>
      </div>
      <div class="tail">
        <span class="dot">
          <span class="d" />
          {{ totalLabel }}
        </span>
      </div>
    </div>

    <div
      v-for="(it, i) in items"
      :key="i"
      class="item"
    >
      <div
        class="ic"
        :class="it.tagCls"
      >
        <svg
          v-if="it.tagCls === 'launch'"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l2.5 7.5h7.5l-6 4.5 2.3 7.5L12 17l-6.3 4.5L8 14l-6-4.5h7.5L12 2z" />
        </svg>
        <svg
          v-else
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M22 11.08V12a10 10 0 11-5.93-9.14"
            stroke-linecap="round"
          />
          <polyline
            points="22 4 12 14.01 9 11.01"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <div class="body">
        <div class="tag-row">
          <span
            class="tag"
            :class="it.tagCls"
            >{{ it.tag }}</span
          >
          <span class="time">{{ it.time }}</span>
          <span
            v-if="it.unread"
            class="unread"
          />
        </div>
        <div class="t">{{ it.title }}</div>
        <div class="m">{{ it.message }}</div>
      </div>
    </div>

    <div
      v-if="items.length === 0"
      class="pa-feed-empty"
    >
      {{ t('property.access.projectNewsFeedTeaser.empty') }}
    </div>

    <div
      v-if="hiddenCount > 0"
      class="pa-feed-gate"
    >
      <div
        class="pa-feed-gate-stack"
        aria-hidden="true"
      >
        <div class="pa-feed-ghost g1" />
        <div class="pa-feed-ghost g2" />
        <div class="pa-feed-ghost g3" />
      </div>
      <div class="pa-feed-gate-card">
        <div class="pa-feed-gate-lock">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="4"
              y="11"
              width="16"
              height="10"
              rx="2"
            />
            <path d="M8 11V8a4 4 0 1 1 8 0v3" />
          </svg>
        </div>
        <div class="pa-feed-gate-text">
          <div class="pa-feed-gate-h">
            {{ t('property.access.projectNewsFeedTeaser.gateHeading', { count: hiddenCount }, hiddenCount) }}
          </div>
          <div class="pa-feed-gate-d">
            {{ t('property.access.projectNewsFeedTeaser.gateDescription') }}
          </div>
        </div>
        <button
          type="button"
          class="pa-feed-gate-cta"
          @click="emit('cta-click')"
        >
          <span
            >{{ t('property.shell.signUpFreeArrow') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></span
          >
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const { t } = useI18n();

const props = defineProps<{
  projectTitle: string;
  items: {
    tag: string;
    tagCls: 'launch' | 'milestone';
    time: string;
    unread: boolean;
    title: string;
    message: string;
  }[];
  hiddenCount: number;
}>();

const emit = defineEmits<{
  (e: 'cta-click'): void;
}>();

const totalCount = computed(() => props.items.length + Math.max(0, props.hiddenCount));
const totalLabel = computed(() =>
  t('property.access.projectNewsFeedTeaser.updatesCount', { count: totalCount.value }, totalCount.value),
);
</script>

<style scoped lang="scss">
.pp-feed {
  margin-top: 22px;
  background: white;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 18px;
  padding: 22px 24px 8px;

  &.pa-feed-guest {
    padding-bottom: 24px;
  }

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;

    .eyebrow {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 700;
      margin-bottom: 4px;
    }

    h3 {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 22px;
      letter-spacing: -0.015em;
      line-height: 1.15;
      margin: 0;
    }

    .tail {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .dot {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
      color: #126b2f;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      .d {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #22c55e;
        box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
      }
    }
  }

  .item {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 0;
    border-top: 1px solid rgba(13, 15, 21, 0.08);

    .ic {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #0d0f15;
      font-size: 14px;

      &.launch {
        background: #fff8ed;
        color: #b8821a;
      }

      &.milestone {
        background: #e6f4ec;
        color: #126b2f;
      }
    }

    .body {
      flex: 1;
      min-width: 0;
    }

    .tag-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
      flex-wrap: wrap;

      .tag {
        padding: 2px 7px;
        border-radius: 5px;
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;

        &.launch {
          background: #ffb648;
          color: #0d0f15;
        }

        &.milestone {
          background: #e6f4ec;
          color: #126b2f;
        }
      }

      .time {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 10px;
        color: rgba(13, 15, 21, 0.62);
        font-weight: 600;
        letter-spacing: 0.02em;
      }

      .unread {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #ff7a59;
      }
    }

    .t {
      font-size: 14px;
      font-weight: 600;
      color: #0d0f15;
      line-height: 1.4;
    }

    .m {
      font-size: 12.5px;
      color: rgba(13, 15, 21, 0.62);
      margin-top: 3px;
      line-height: 1.4;
    }
  }
}

.pa-feed-empty {
  padding: 18px 0;
  font-size: 13px;
  color: rgba(13, 15, 21, 0.62);
  text-align: center;
  border-top: 1px solid rgba(13, 15, 21, 0.08);
}

.pa-feed-gate {
  position: relative;
  margin-top: 18px;
  padding-top: 64px;
}

.pa-feed-gate-stack {
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  height: 64px;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
}

.pa-feed-ghost {
  position: absolute;
  inset-inline-start: 0;
  height: 16px;
  border-radius: 6px;
  background: rgba(13, 15, 21, 0.05);

  &.g1 {
    top: 0;
    width: 75%;
  }

  &.g2 {
    top: 22px;
    width: 60%;
    opacity: 0.7;
  }

  &.g3 {
    top: 44px;
    width: 50%;
    opacity: 0.45;
  }
}

.pa-feed-gate-card {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  background: linear-gradient(180deg, #fff8ed, #fff);
  border: 1px solid rgba(255, 182, 72, 0.4);
  box-shadow: 0 4px 14px rgba(255, 182, 72, 0.1);
}

.pa-feed-gate-lock {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #ffb648;
  color: #0d0f15;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pa-feed-gate-h {
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: #0d0f15;
  margin-bottom: 3px;
}

.pa-feed-gate-d {
  font-size: 12.5px;
  color: rgba(13, 15, 21, 0.7);
  line-height: 1.45;
}

.pa-feed-gate-cta {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  background: #0d0f15;
  color: white;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #25272c;
  }
}
</style>
